import * as React from "react";

import { cn } from "@/lib/utils";
import { IconName } from "lucide-react/dynamic";
import { HeaderGradientColors } from "../blocks/types";
import { HeaderWithIcon } from "./header-with-icon";
import { useFlowStore } from "@/stores/flow-store";
import { AlertTriangle } from "lucide-react";

interface NodeCardProps extends React.HTMLAttributes<HTMLDivElement> {
  nodeId?: string;
}

const NodeCard = React.forwardRef<HTMLDivElement, NodeCardProps>(
  ({ className, nodeId, ...props }, ref) => {
    const { actions: { validation: actions } } = useFlowStore();
    const hasErrors = nodeId ? actions.getNodeErrors(nodeId).length > 0 : false;

    return (
      <div
        ref={ref}
        className={cn(
          "w-72 border rounded-xl bg-card/50 shadow-sm backdrop-blur-xl transition divide-y divide-card-foreground/10 data-[selected=true]:border-primary relative",
          hasErrors 
            ? "border-red-500/50 shadow-red-500/20" 
            : "border-card-foreground/10",
          className
        )}
        {...props}
      >
        {hasErrors && (
          <div className="absolute -top-1 -right-1 z-10">
            <div className="flex items-center justify-center w-5 h-5 bg-red-500 rounded-full">
              <AlertTriangle className="w-3 h-3 text-white" />
            </div>
          </div>
        )}
        {props.children}
      </div>
    );
  }
);
NodeCard.displayName = "NodeCard";

interface NodeCardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  icon: IconName;
  title: string;
  handleDeleteNode: () => void;
  handleShowNodeProperties: () => void;
  gradientColor?: keyof typeof HeaderGradientColors;
}

const NodeCardHeader = React.forwardRef<HTMLDivElement, NodeCardHeaderProps>(
  (
    {
      className,
      icon,
      title,
      handleDeleteNode,
      handleShowNodeProperties,
      gradientColor,
      ...props
    },
    ref
  ) => {
    const gradientColorClass =
      HeaderGradientColors[gradientColor as keyof typeof HeaderGradientColors];
    return (
      <div
        ref={ref}
        className={`relative overflow-clip rounded-t-xl bg-card/50 ${className}`}
        {...props}
      >
        <div className="absolute inset-0">
          <div
            className={`absolute h-full w-full ${gradientColor ? gradientColorClass : "bg-primary/40"
              }`}
          />
        </div>

        <div className="relative h-9 flex items-center justify-between gap-x-4 px-0.5 py-0.5">
          <div className="flex grow items-center pl-1">
            <HeaderWithIcon icon={icon} title={title} />
          </div>
        </div>
      </div>
    );
  }
);
NodeCardHeader.displayName = "NodeCardHeader";

interface NodeCardDescriptionProps
  extends React.HTMLAttributes<HTMLDivElement> {
  description: string;
}

const NodeCardDescription = React.forwardRef<
  HTMLDivElement,
  NodeCardDescriptionProps
>(({ className, description, ...props }, ref) => (
  <div ref={ref} className={cn("px-4 py-2", className)} {...props}>
    <div className="text-xs text-card-foreground">{description}</div>
  </div>
));
NodeCardDescription.displayName = "NodeCardDescription";

const NodeCardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex flex-col divide-y divide-card-foreground/10",
      className
    )}
    {...props}
  />
));
NodeCardContent.displayName = "NodeCardContent";

interface NodeCardFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  nodeId: string | number;
}

const NodeCardFooter = React.forwardRef<HTMLDivElement, NodeCardFooterProps>(
  ({ className, nodeId, ...props }, ref) => (
    <div
      ref={ref}
      className={`bg-card-foreground/10 overflow-clip rounded-b-md px-4 py-2 text-[10px] text-card-foreground/50 ${className}`}
      {...props}
    >
      Node: <span className="font-semibold">#{nodeId}</span>
    </div>
  )
);
NodeCardFooter.displayName = "NodeCardFooter";

export {
  NodeCard, NodeCardContent, NodeCardDescription, NodeCardFooter, NodeCardHeader
};

